import type {
  CreateGenreRequest,
  Genre,
  UpdateGenreRequest,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys, snakeCase } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export interface ListGenreOptions {
  onlyEnabled?: boolean;
  page?: number;
  perPage?: number;
  sortField?: keyof Genre | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listGenres(
  options: ListGenreOptions = {}
): Promise<{ data: Genre[]; total?: number }> {
  const {
    onlyEnabled = false,
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase.from('genres').select('*', selectOptions);

  if (onlyEnabled) {
    query = query.eq('enabled', true);
  }

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('name', { ascending: true });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch genres: ${error.message}`);
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<Genre>(row as Record<string, unknown>)
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export async function getGenre(id: string): Promise<Genre> {
  const { data, error } = await supabase
    .from('genres')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch genre: ${error.message}`);
  }

  if (!data) {
    throw new Error('Genre not found');
  }

  return fromDbKeys<Genre>(data as Record<string, unknown>);
}

export async function createGenre(
  genreData: CreateGenreRequest
): Promise<Genre> {
  const { data, error } = await supabase
    .from('genres')
    .insert(toDbKeys(genreData))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create genre: ${error.message}`);
  }

  return fromDbKeys<Genre>(data as Record<string, unknown>);
}

export async function updateGenre(
  id: string,
  genreData: UpdateGenreRequest
): Promise<Genre> {
  const { data, error } = await supabase
    .from('genres')
    .update(toDbKeys(genreData))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update genre: ${error.message}`);
  }

  return fromDbKeys<Genre>(data as Record<string, unknown>);
}

export async function deleteGenre(id: string): Promise<void> {
  const { error } = await supabase.from('genres').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete genre: ${error.message}`);
  }
}

export async function getGenreInstructions(id: string): Promise<string> {
  const { data, error } = await supabase
    .from('genres')
    .select('instructions')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch genre instructions: ${error.message}`);
  }

  return typeof data?.instructions === 'string' ? data.instructions : '';
}

// Backwards compatibility helper for callers expecting only enabled genres
export async function getAllGenres(): Promise<Genre[]> {
  const { data } = await listGenres({ onlyEnabled: true });
  return data;
}

// Helper for edge functions that need all genres (including disabled)
export async function getGenres(): Promise<Genre[]> {
  const { data } = await listGenres({ onlyEnabled: false });
  return data;
}

/**
 * Count shows for a genre and update the show_count field
 * More reliable than increment/decrement since it always reflects the true count
 */
export async function updateGenreShowCount(genreId: string): Promise<void> {
  // Count shows for this genre
  const { count, error: countError } = await supabase
    .from('shows')
    .select('id', { count: 'exact', head: true })
    .eq('genre_id', genreId);

  if (countError) {
    throw new Error(
      `Failed to count shows for genre ${genreId}: ${countError.message}`
    );
  }

  // Update the genre with the count
  const { error: updateError } = await supabase
    .from('genres')
    .update({ show_count: count ?? 0 })
    .eq('id', genreId);

  if (updateError) {
    throw new Error(
      `Failed to update show_count for genre ${genreId}: ${updateError.message}`
    );
  }
}
